<template>
    <div v-click-outside="close" class="btn-wallet" @click.stop="open = !open">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" class="wallet-icon-svg">
            <path d="M21 7H5a2 2 0 010-4h13a1 1 0 011 1v3M3 5v14a2 2 0 002 2h15a1 1 0 001-1v-4M21 12v4h-4a2 2 0 010-4h4z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="wallet-label">{{ selected.name }} : {{ selected.currency }} {{ selected.display }}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" class="wallet-chevron" :class="{ open }">
            <path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>

        <transition name="menu-drop">
            <div v-if="open" class="wallet-dropdown" @click.stop>
                <div class="wallet-dropdown-title">{{ $t('nav.yourBalances') }}</div>
                <div
                    v-for="w in wallets"
                    :key="w.key"
                    class="wallet-row"
                    :class="{ active: selectedKey === w.key }"
                    @click.stop="select(w.key)"
                >
                    <div class="wallet-row-text">
                        <span class="wallet-row-name">{{ w.name }}</span>
                    </div>
                    <span class="wallet-row-amount">{{ w.display }} {{ w.currency }}</span>
                    <span class="wallet-row-radio" :class="{ active: selectedKey === w.key }"></span>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { formatPoint } from '@/core/utils/format';

export default {
    name: 'NavWallet',
    data() {
        return { open: false };
    },
    computed: {
        selectedKey() {
            return this.$sharedState.selectedWallet;
        },
        wallets() {
            const info = this.$sharedState.memberInfo;
            const currency = info.currency || '';
            return [
                { key: 'main', name: this.$t('nav.mainWallet'), display: formatPoint(info.point), currency },
                { key: 'chip', name: this.$t('nav.chipWallet'), display: formatPoint(info.point2), currency },
                { key: 'token', name: this.$t('nav.tokenWallet'), display: formatPoint(info.point3), currency }
            ];
        },
        selected() {
            return this.wallets.find(w => w.key === this.selectedKey) || this.wallets[0];
        }
    },
    methods: {
        close() {
            this.open = false;
        },
        select(key) {
            this.$sharedState.selectedWallet = key;
            localStorage.setItem('selectedWallet', key);
            this.open = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.btn-wallet {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--primary);
    border-right: none;
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    padding: 7px 14px;
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;
    white-space: nowrap;
    min-width: 220px;

    &:last-child {
        border-right: 1px solid var(--primary);
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
    }

    @media (max-width: 768px) {
        padding: 6px 10px;
        gap: 6px;
        font-size: 12px;
        min-width: 160px;
        max-width: 180px;
    }
}

.wallet-icon-svg {
    color: var(--highlight);
    flex-shrink: 0;
}

.wallet-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.wallet-chevron {
    color: var(--accent);
    flex-shrink: 0;
    transition: transform 0.2s ease;

    &.open {
        transform: rotate(180deg);
    }
}

.wallet-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 100%;
    padding: 14px;
    background: linear-gradient(165deg, var(--surface-3) 0%, var(--surface-0) 100%);
    border: 1px solid var(--border);
    border-radius: 14px;
    z-index: 400;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--black) 50%, transparent);
    cursor: default;
    white-space: normal;
}

.wallet-dropdown-title {
    color: var(--text);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 10px;
}

.wallet-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s;

    & + .wallet-row {
        margin-top: 4px;
    }

    &:hover,
    &.active {
        background: var(--surface-hover);
    }
}

.wallet-row-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.wallet-row-name {
    color: var(--text);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.wallet-row-amount {
    flex-shrink: 0;
    color: var(--text);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
}

.wallet-row-radio {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 1px solid var(--text-dim);

    &.active {
        border-color: var(--success);
        background: var(--success);
    }
}

.menu-drop-enter-active,
.menu-drop-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-drop-enter,
.menu-drop-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
